// steps/SonstigesStep.tsx

import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Typography, TextField, ToggleButton, ToggleButtonGroup, Select, MenuItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";

import { useAllowScroll } from "../AllowScrollContext";
import { FuhrparkSummary, MaterialSummary, SummaryContainer, TaktSummary, UhrzeitSummary } from "./Summaries";
import { MaterialOrder } from "@/components/planning/Project";

export default function SonstigesStep({
  order,
  expertMode,
  onBack,
  onSelect,
  isDispo,
}: {
  order: MaterialOrder;
  expertMode: boolean;
  isDispo: boolean;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(false);
  }, [setAllowScroll]);

  const [easyTarget, setEasyTarget] = useState(order.easyTarget ?? "SITE");
  const [begleitschein, setBegleitschein] = useState(!!order.begleitschein);
  const [comment, setComment] = useState(order.comment ?? "");
  const [transportComment, setTransportComment] = useState(order.transportComment ?? "");

  const handleConfirm = () => {
    onSelect({
      easyTarget,
      begleitschein,
      comment,
      transportComment,
    });
  };

  useEffect(() => {
    if (!expertMode) return;
    handleConfirm();
  }, [easyTarget, begleitschein, comment, transportComment, expertMode]);

  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {!expertMode && (
        <Fragment>
          <SummaryContainer>
            <MaterialSummary includeAmount order={order} isBauhof={isBauhof} isBaustellendienst={isBaustellendienst} />
            <UhrzeitSummary order={order} />
            <FuhrparkSummary order={order} />
          </SummaryContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 18 }}>Sonstiges</Typography>
          </Box>
        </Fragment>
      )}

      {/* Lieferort */}
      <Fragment>
        <Typography variant="button">Lieferort</Typography>

        <Box sx={{ mb: 1, display: "flex" }}>
          <Box
            sx={{
              minWidth: 170,
              height: 30,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              border: "0.75px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Select
              variant="standard"
              disableUnderline
              value={easyTarget}
              onChange={(e) => {
                const val = e.target.value as "SITE" | "LAGER";
                if (val) setEasyTarget(val);
              }}
              sx={{
                height: 30,
                width: "100%",
                px: 1,
                display: "flex",
                alignItems: "center",
                "& .MuiSelect-select": {
                  py: 0,
                  display: "flex",
                  alignItems: "center",
                  height: 30,
                },
              }}
            >
              <MenuItem value="SITE">Baustelle</MenuItem>
              <MenuItem value="LAGER">Lagerplatz</MenuItem>
            </Select>
          </Box>
        </Box>
      </Fragment>

      {/* Begleitschein (nur im Expertenmodus) */}
      {expertMode ? (
        <Box sx={{ mb: 1 }}>
          <Typography variant="button">Begleitschein benötigt</Typography>

          <Box sx={{ mt: 0 }}>
            <ToggleButtonGroup
              exclusive
              value={begleitschein ? "YES" : "NO"}
              onChange={(_, next) => {
                if (!next) return; // ignore deselect
                setBegleitschein(next === "YES");
              }}
              size="small"
            >
              <ToggleButton value="YES">Ja</ToggleButton>
              <ToggleButton value="NO">Nein</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      ) : null}

      {/* Kommentar */}
      <Typography variant="button">{isBauhof ? "Weitere Informationen" : "Fahrer-Informationen"}</Typography>

      <TextField value={comment} onChange={(e) => setComment(e.target.value)} multiline minRows={3} fullWidth sx={{ mb: "10px" }} />

      {isDispo ? (
        <Fragment>
          <Typography variant="button">Dispo-Informationen</Typography>

          <TextField
            value={transportComment}
            onChange={(e) => setTransportComment(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={{ mb: "10px" }}
          />
        </Fragment>
      ) : null}

      {!expertMode && (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ minWidth: 44, px: 1 }}>
            zurück
          </Button>

          <Button startIcon={<CheckIcon />} variant="contained" color="success" onClick={handleConfirm} sx={{ flex: 1 }}>
            Weiter
          </Button>
        </Box>
      )}
    </Box>
  );
}
